import { APP_INITIALIZER, DOCUMENT, Inject, Injectable } from '@angular/core';
import {
  BootstrapperBase,
  LogService,
  TOKENS as COMMON_TOKENS,
} from '@trustedshops/tswp-core-common';
import {
  KameleoonService,
  TOKENS as MASTERDATA_TOKENS,
} from '@trustedshops/tswp-core-masterdata';
import { AccountService } from '../services/account.service';
import { CompanyService } from '../services/company.service';
import { ChannelsService } from '../services/channels.service';
import { SDKParameters } from '@kameleoon/javascript-sdk';
import { Environment, ENVIRONMENT } from '../models/environment.type';
import { firstValueFrom, Observable, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  take,
} from 'rxjs/operators';
import { UserCentricsConsent } from '../core/user-centrics-consent';

@Injectable()
export class KameleoonBootstrapper
  extends UserCentricsConsent
  implements BootstrapperBase
{
  private static readonly TYPE: string = 'KameleoonBootstrapper';

  /**
   * Max time to wait for the initial country code before booting Kameleoon
   * without it. The country code is an optional targeting enhancement, so it
   * must never block app bootstrap if the channel/SEO lookup is slow or stalls;
   * the live updates subscription backfills it once it becomes available.
   */
  private static readonly INITIAL_COUNTRY_CODE_TIMEOUT_MS = 3_000;

  constructor(
    @Inject(MASTERDATA_TOKENS.KameleoonService)
    private readonly kameleoonService: KameleoonService,

    @Inject(COMMON_TOKENS.LogService) private readonly logService: LogService,

    private readonly accountService: AccountService,

    private readonly companyService: CompanyService,

    private readonly channelsService: ChannelsService,

    @Inject(ENVIRONMENT) private readonly environment: Environment,

    @Inject(DOCUMENT) readonly _document: Document
  ) {
    super(_document);
  }

  private get clientParameters(): SDKParameters {
    return {
      siteCode: this.environment.kameleoon.clientParameters.siteCode,
      configuration: {
        updateInterval:
          this.environment.kameleoon.clientParameters.configuration
            .updateInterval,
        environment:
          this.environment.kameleoon.clientParameters.configuration.environment,
        cookieDomain:
          this.environment.kameleoon.clientParameters.configuration
            .cookieDomain,
      },
    };
  }

  /**
   * APP_INITIALIZER entry point.
   *
   * Resolves only after Kameleoon has actually initialized (or failed to
   * initialize). Never rejects: any failure is logged and swallowed so that
   * Angular bootstrap is not aborted by a third-party SDK issue.
   */
  public async initialize(): Promise<void> {
    this.logService.information(
      KameleoonBootstrapper.TYPE,
      'Initializing Kameleoon'
    );
    try {
      const hasKameleoonConsent = await this.hasKameleoonConsent();
      // Record the resolved consent on the service so consumers (e.g. the
      // upgrade-journey components) can fall back to local defaults when
      // Kameleoon is unavailable due to missing consent.
      this.kameleoonService.setConsent(hasKameleoonConsent);
      if (!hasKameleoonConsent) {
        this.logService.debug(
          KameleoonBootstrapper.TYPE,
          'Kameleoon consent not granted, skipping initialization'
        );
        return;
      }

      const accountId = await new Promise<string>((resolve, reject) =>
        this.accountService.accountId$
          .pipe(
            filter((id): id is string => !!id),
            take(1)
          )
          .subscribe({ next: resolve, error: reject })
      );

      // Multicast the selected channel's country code so the SEO settings are
      // fetched once and shared between the initial value and live updates.
      const countryCode$ = this.#countryCode$();

      const [company, initialCountryCode] = await Promise.all([
        firstValueFrom(
          this.companyService.company$.pipe(filter(Boolean), take(1))
        ).catch((error) => {
          this.logService.debug(
            KameleoonBootstrapper.TYPE,
            'Company unavailable; initializing Kameleoon without segment',
            error
          );
          return undefined;
        }),
        this.#resolveInitialCountryCode(countryCode$),
      ]);

      await this.kameleoonService.initialize(
        this.clientParameters,
        accountId,
        initialCountryCode,
        company?.customerSegment
      );

      this.#subscribeToCountryCodeUpdates(countryCode$, initialCountryCode);

      this.logService.information(
        KameleoonBootstrapper.TYPE,
        'Kameleoon initialized successfully'
      );
    } catch (error) {
      this.logService.error(
        KameleoonBootstrapper.TYPE,
        new Error(`Kameleoon initialization failed: ${JSON.stringify(error)}`)
      );
    }
  }

  private async hasKameleoonConsent(): Promise<boolean> {
    return await this.userConsentObtained('Kameleoon');
  }

  /**
   * Reads the current country code, but never blocks app bootstrap on it: if the
   * channel/SEO lookup does not resolve within {@link INITIAL_COUNTRY_CODE_TIMEOUT_MS},
   * it falls back to `undefined` and the updates subscription backfills the value
   * once it becomes available.
   */
  async #resolveInitialCountryCode(
    countryCode$: Observable<string | undefined>
  ): Promise<string | undefined> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const fallback = new Promise<undefined>((resolve) => {
      timer = setTimeout(
        () => resolve(undefined),
        KameleoonBootstrapper.INITIAL_COUNTRY_CODE_TIMEOUT_MS
      );
    });

    try {
      return await Promise.race([
        firstValueFrom(countryCode$.pipe(take(1))).catch((error) => {
          this.logService.debug(
            KameleoonBootstrapper.TYPE,
            'Country code unavailable; initializing Kameleoon without it',
            error
          );
          return undefined;
        }),
        fallback,
      ]);
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * Resilient, multicast stream of the selected channel's trimmed `targetMarket`
   * (or `undefined` when no channel is selected or the SEO lookup fails).
   *
   * `shareReplay(1)` fetches the SEO settings once and shares the result between
   * the initial Kameleoon value and the live country-code updates, so selecting
   * a channel does not trigger two SEO requests. A failed lookup yields
   * `undefined` rather than tearing the stream down, so later channel switches
   * still update the country code.
   */
  #countryCode$(): Observable<string | undefined> {
    return this.channelsService.selectedChannelId$.pipe(
      switchMap((channelId) =>
        channelId
          ? this.channelsService.getSeoSettings(channelId).pipe(
              map((settings) => settings?.targetMarket?.trim() || undefined),
              catchError((error) => {
                this.logService.error(
                  KameleoonBootstrapper.TYPE,
                  new Error(
                    `Get SEO settings for country code failed: ${JSON.stringify(
                      error
                    )}`
                  )
                );
                return of(undefined);
              })
            )
          : of(undefined)
      ),
      distinctUntilChanged(),
      shareReplay(1)
    );
  }

  #subscribeToCountryCodeUpdates(
    countryCode$: Observable<string | undefined>,
    initialCountryCode?: string
  ): void {
    countryCode$
      .pipe(
        filter((countryCode): countryCode is string => !!countryCode),
        // The shared stream replays the value already applied during
        // initialization; drop that first emission only while it still matches
        // what was applied, so a channel switch mid-init is not lost.
        filter(
          (countryCode, index) =>
            index > 0 || countryCode !== initialCountryCode
        )
      )
      .subscribe((targetMarket) => {
        void this.kameleoonService
          .setCountryCode(targetMarket)
          .catch((error) =>
            this.logService.error(
              KameleoonBootstrapper.TYPE,
              new Error(`setCountryCode failed: ${JSON.stringify(error)}`)
            )
          );
      });
  }
}

export const KameleoonInitializer = [
  KameleoonBootstrapper,
  {
    provide: APP_INITIALIZER,
    multi: true,
    useFactory: (initializer: BootstrapperBase) => {
      return () => initializer.initialize();
    },
    deps: [KameleoonBootstrapper],
  },
];
